/**
 * 매장 케어 — 추가 · 수정 모달과 노출 스위치 (/my/guide/ · page-partner-guide.php).
 *
 * 틀(카테고리 · 타이틀 · 간단 설명 · 관련상품)은 PHP 가 그리고, 설명글은 설정 화면과 같은
 * 공용 편집기(richText)를 비워 둔 자리에 끼운다. 여닫기는 initModal.js 가 맡는다.
 *
 * 모달은 하나다. [+ 매장 케어 추가]로 열면 빈 추가 모드, 카드를 누르면 그 카드의
 * data-guide-edit 값으로 채운 수정 모드가 된다. 관리자 글이면 "저장하면 내 글로 복사된다"는 안내를 띄우고
 * 삭제 버튼을 감춘다(관리자 글은 지울 수 없다 — 판단은 매장 사이트가 한 번 더 한다).
 *
 * 관련상품은 상품 검색 모달(productPicker.js)에서 담고 빼며, 폼 안 목록(.guide-products)에 사진으로 놓인다.
 * 카드의 노출 스위치는 누르는 즉시 저장하고 목록을 새로고침한다.
 * 저장 · 삭제 · 순서는 소식과 같은 조각(partnerForm.js)으로 admin-ajax(Partner_Guide)를 거쳐 매장 사이트로 간다.
 */

import { lockModal } from './initModal';
import { readOrder } from './initOrderList';
import {
  postForm,
  reloadWith as reloadWithKey,
  setNotice,
  showFlash,
  showToast,
} from './partnerForm';
import { createProductPicker } from './productPicker';
import { createModeEditor } from './modeEditor';
import createAuthorField from './partnerAuthor';

/** 새로고침한 화면에 띄울 알림을 맡겨 두는 자리 */
const FLASH_KEY = 'qrshop_guide_flash';

const reloadWith = (message, url = '') => reloadWithKey(FLASH_KEY, message, url);

const InitPartnerGuide = () => {
  const form = document.querySelector('[data-guide-form]');

  if (!form) {
    return;
  }

  showFlash(FLASH_KEY);

  // 폼은 모달 body 이고, 저장·삭제 버튼과 결과 안내는 footer 에 있다 (저장 버튼은 form 속성으로 이어진다)
  const dialog = form.closest('dialog');
  const status = dialog?.querySelector('[data-guide-status]');
  const submit = dialog?.querySelector(`[type="submit"][form="${form.getAttribute('id')}"]`);
  const deleteButton = dialog?.querySelector('[data-guide-delete]');
  const copyNote = form.querySelector('[data-guide-copy]');
  // 공통 글 — 남이 열면 "복사된다", 쓴 통합 계정이 열면 "모든 매장에 반영된다"
  const sharedCopyNote = form.querySelector('[data-guide-shared-copy]');
  const sharedOwnNote = form.querySelector('[data-guide-shared-own]');
  const author = createAuthorField(form);
  const locked = form.querySelector('[data-guide-locked]');
  const contentFields = form.querySelectorAll('[data-guide-content]');
  const productsBox = form.querySelector('[data-guide-products]');
  const productsEmpty = form.querySelector('[data-guide-products-empty]');
  const idInput = form.elements.namedItem('post_id');
  const titleInput = form.elements.namedItem('title');
  const summaryInput = form.elements.namedItem('summary');
  const categorySelect = form.elements.namedItem('category');
  const relatedMax = Number(form.dataset.relatedMax || 20);

  // 지금 모달에 들어 있는 대상 — 'add' 또는 'edit-{id}'
  let target = 'add';

  /** 담긴 관련상품 — 순서대로. {product_id, name, brand, thumbnail} */
  let related = [];

  const setStatus = (message, state = 'error') => setNotice(status, message, state);

  const body = createModeEditor({ placeholder: '설명글을 입력해 주세요.' });
  form.querySelector('[data-guide-body]')?.append(body.element);

  // 라벨 줄 오른쪽 — Simple / Pro 전환 버튼
  form.querySelector('[data-guide-body-mode]')?.replaceWith(body.switcher);

  // 고치기 시작하면 지난번 안내는 걷어 낸다
  form.addEventListener('input', () => setStatus(''));

  // ── 관련상품 ────────────────────────────────────────────────────────
  const pickerDialog = document.querySelector('[data-product-picker]');
  let picker = null;

  const isRelated = (productId) => related.some((product) => product.product_id === productId);

  /** 폼 안 관련상품 목록을 다시 그린다 */
  const renderRelated = () => {
    if (!productsBox) {
      return;
    }

    productsBox.textContent = '';

    related.forEach((product) => {
      const item = document.createElement('div');
      item.className = 'guide-products__item';

      const figure = document.createElement('div');
      figure.className = 'guide-products__thumb';

      if (product.thumbnail) {
        const image = document.createElement('img');
        image.src = product.thumbnail;
        image.alt = '';
        image.loading = 'lazy';
        figure.append(image);
      }

      const name = document.createElement('p');
      name.className = 'guide-products__name';
      name.textContent = product.name;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'guide-products__remove';
      remove.setAttribute('aria-label', `「${product.name}」 빼기`);
      remove.addEventListener('click', () => {
        related = related.filter((row) => row.product_id !== product.product_id);
        renderRelated();
        picker?.sync();
      });

      item.append(figure, name, remove);
      productsBox.append(item);
    });

    if (productsEmpty) {
      productsEmpty.hidden = related.length > 0;
    }

    // 검색 모달의 "담음" 개수는 목록에 없는 상품도 세야 하므로 여기서 알려 준다
    const selected = pickerDialog?.querySelector('[data-product-selected]');

    if (selected) {
      selected.dataset.count = String(related.length);
    }
  };

  if (pickerDialog instanceof HTMLDialogElement) {
    picker = createProductPicker({
      dialog: pickerDialog,
      root: form,
      isSelected: isRelated,
      onToggle: (product) => {
        if (isRelated(product.product_id)) {
          related = related.filter((row) => row.product_id !== product.product_id);
        } else if (related.length >= relatedMax) {
          showToast(`관련상품은 ${relatedMax}개까지 붙일 수 있습니다.`, 'warning');
          return;
        } else {
          related = [...related, product];
        }

        renderRelated();
      },
    });
  }

  // ── 모달 채우기 ─────────────────────────────────────────────────────
  /** 원장이 고치면 깨지는 글(관리자가 넣은 영상 등)은 설명글 칸을 감추고 안내를 띄운다 */
  const setEditable = (editable) => {
    contentFields.forEach((field) => {
      field.hidden = !editable;
    });

    if (locked) {
      locked.hidden = editable;
    }
  };

  /** 모든 칸을 처음 상태로 */
  const clear = () => {
    form.reset();

    // hidden 입력은 value 가 곧 기본값이라 reset() 으로 비워지지 않는다.
    // 남겨 두면 [추가]가 방금 고치던 글을 덮어쓴다 — 직접 비운다.
    idInput.value = '';
    body.setContent('');
    related = [];
    renderRelated();
    setStatus('');
    setEditable(true);
    author.reset();

    [copyNote, sharedCopyNote, sharedOwnNote].forEach((note) => {
      if (note) {
        note.hidden = true;
      }
    });
  };

  const openAdd = () => {
    if ('add' === target) {
      return;
    }

    target = 'add';
    clear();

    if (deleteButton) {
      deleteButton.hidden = true;
    }
  };

  /**
   * @param {{ id: number, title: string, category: number, summary: string, body: string, mode: string, locked: boolean,
   *   related: Array<{ product_id: number, name: string, brand: string, thumbnail: string }>, mine: boolean,
   *   shared: boolean, can_edit: boolean }} guide
   */
  const openEdit = (guide) => {
    const next = `edit-${guide.id}`;

    if (next === target) {
      return;
    }

    target = next;
    clear();

    idInput.value = String(guide.id);
    titleInput.value = guide.title || '';
    summaryInput.value = guide.summary || '';

    // 목록에 없는 카테고리면 빈 값(고르라는 자리)이 남는다 — 저장하려면 골라야 한다
    if (
      categorySelect &&
      [...categorySelect.options].some((option) => option.value === String(guide.category))
    ) {
      categorySelect.value = String(guide.category);
    }

    body.setContent(guide.body || '', guide.mode);
    setEditable(true !== guide.locked);

    related = Array.isArray(guide.related)
      ? guide.related.map((product) => ({
          product_id: Number(product.product_id),
          name: product.name || '',
          brand: product.brand || '',
          thumbnail: product.thumbnail || '',
        }))
      : [];
    renderRelated();

    // 복사 없이 고치고 지울 수 있는 글 — 내 글, 또는 내가 쓴 공통 글 (옛 플러그인은 can_edit 이 없어 mine 을 따른다)
    const canEdit = undefined === guide.can_edit ? true === guide.mine : true === guide.can_edit;
    const shared = true === guide.shared;

    author.lock(shared);

    if (copyNote) {
      copyNote.hidden = canEdit || shared;
    }

    if (sharedCopyNote) {
      sharedCopyNote.hidden = canEdit || !shared;
    }

    if (sharedOwnNote) {
      sharedOwnNote.hidden = !(canEdit && shared);
    }

    if (deleteButton) {
      deleteButton.hidden = !canEdit;
    }
  };

  // 여는 일은 initModal.js 가 한다. 여기서는 무엇으로 열었는지 보고 내용만 채운다.
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-partner-add]')) {
      openAdd();
      return;
    }

    const card = event.target.closest?.('[data-guide-edit]');

    if (!card) {
      return;
    }

    try {
      openEdit(JSON.parse(card.dataset.guideEdit || '{}'));
    } catch (error) {
      setStatus('케어 글을 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.');
    }
  });

  // ── 저장 · 삭제 ─────────────────────────────────────────────────────
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const title = titleInput instanceof HTMLInputElement ? titleInput.value.trim() : '';
    const category = Number(categorySelect?.value || 0);

    if (!category) {
      setStatus(
        categorySelect?.disabled
          ? '카테고리를 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.'
          : '카테고리를 골라 주세요.'
      );
      categorySelect?.focus();
      return;
    }

    if ('' === title) {
      setStatus('타이틀을 입력해 주세요.');
      titleInput?.focus();
      return;
    }

    const summary = (summaryInput?.value || '').trim();

    if ('' === summary) {
      setStatus('간단 설명을 입력해 주세요.');
      summaryInput?.focus();
      return;
    }

    // 설명글 칸이 잠긴 글(관리자 화면에서 만든 본문)은 본문을 보내지 않으므로 여기서 묻지 않는다
    const bodyHTML = body.getHTML();
    const bodyLocked = locked ? !locked.hidden : false;

    if (
      !bodyLocked &&
      '' ===
        bodyHTML
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim()
    ) {
      setStatus('설명글을 입력해 주세요.');
      body.focus();
      return;
    }

    // 추가 모드면 어떤 경우에도 새 글이다 (남의 글을 덮어쓰지 않도록 한 번 더 막는다)
    const id = 'add' === target ? 0 : Number(idInput.value || 0);

    const unlock = lockModal(dialog, submit, '저장 중…');

    try {
      const result = await postForm(form, form.dataset.action || '', {
        id,
        category,
        title,
        summary,
        body: bodyHTML,
        mode: body.getMode(),
        // 새 글에서만 뜻이 있다 — 고칠 때는 매장 사이트가 원래 작성자를 그대로 둔다
        author: id ? 'shop' : author.value(),
        related: related.map((product) => product.product_id).join(','),
      });

      // 끝났으면 먼저 닫는다. 새 글 · 복사한 글은 노출 목록 맨 위에 들어가므로 그 목록으로 옮기고,
      // 내 글을 고친 것은 보던 목록 그대로 다시 그린다.
      dialog.close();
      unlock();
      reloadWith(
        result.message || '저장했습니다.',
        id && !result.copied ? '' : form.dataset.listUrl || ''
      );
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  deleteButton?.addEventListener('click', async () => {
    const id = Number(idInput.value || 0);

    if (!id) {
      return;
    }

    // 매장 사이트에서는 휴지통으로 가지만 원장은 되살릴 방법이 없으므로 한 번 묻는다
    const sharedPost = sharedOwnNote ? !sharedOwnNote.hidden : false;

    if (
      !window.confirm(
        sharedPost
          ? '이 공통 케어 글을 삭제할까요?\n이 글을 켠 모든 매장 페이지에서 바로 사라집니다.'
          : '이 케어 글을 삭제할까요?\n매장 페이지에서도 바로 사라집니다.'
      )
    ) {
      return;
    }

    const unlock = lockModal(dialog, deleteButton, '삭제 중…');

    try {
      const result = await postForm(form, form.dataset.deleteAction || '', { id });

      dialog.close();
      unlock();
      reloadWith(result.message || '삭제했습니다.');
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  // ── 카드의 노출 스위치 — 바꾸는 즉시 저장한다 ─────────────────────────
  document.addEventListener('change', async (event) => {
    const toggle = event.target;

    if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-guide-show]')) {
      return;
    }

    const id = Number(toggle.dataset.guideShow || 0);
    const shown = toggle.checked;
    const label = toggle.closest('.toggle')?.querySelector('.toggle__label');

    toggle.disabled = true;

    if (label) {
      label.textContent = '저장 중…';
    }

    try {
      const result = await postForm(form, form.dataset.showAction || '', {
        id,
        shown: shown ? 1 : 0,
      });

      reloadWith(
        result.message || (shown ? '매장 페이지에 보입니다.' : '매장 페이지에서 감췄습니다.')
      );
    } catch (error) {
      toggle.checked = !shown;
      toggle.disabled = false;

      if (label) {
        label.textContent = toggle.checked ? '사용' : '미사용';
      }

      showToast(error.message, 'error');
    }
  });

  // ── 순서 변경 모달 — 노출 중인 케어가 둘 이상일 때만 있다 ──────────────
  const orderDialog = document.getElementById('guide-order-modal');
  const orderList = orderDialog?.querySelector('[data-order-list]');
  const orderSave = orderDialog?.querySelector('[data-order-save]');
  const orderStatus = orderDialog?.querySelector('[data-order-status]');

  orderSave?.addEventListener('click', async () => {
    setNotice(orderStatus, '');

    const unlock = lockModal(orderDialog, orderSave, '저장 중…');

    try {
      const result = await postForm(form, form.dataset.orderAction || '', {
        ids: readOrder(orderList).join(','),
      });

      orderDialog.close();
      unlock();
      reloadWith(result.message || '순서를 바꿨습니다.');
    } catch (error) {
      unlock();
      setNotice(orderStatus, error.message);
    }
  });
};

export default InitPartnerGuide;
